import * as React from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { Button } from './Button';
import { Surface } from './Card';
import { Checkbox, Switch } from './Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './DatePicker';
import { Inline, Stack } from './Layout';
import { ListRow } from './List';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select';
import { Text } from './Text';

export interface TimeSlot {
  /** "HH:MM", 24-hour. */
  start: string;
  end: string;
}

export interface DayAvailability {
  timeSlots: TimeSlot[];
  open24Hours: boolean;
  closed: boolean;
}

/** A week of opening hours, keyed by lowercase day name: `monday` … `sunday`. */
export type AvailabilityData = Record<string, DayAvailability>;

/** [key, full name, short name] for each day, Monday first. */
export type HoursDay = [string, string, string];

const DAYS: HoursDay[] = [
  ['monday', 'Monday', 'Mon'],
  ['tuesday', 'Tuesday', 'Tue'],
  ['wednesday', 'Wednesday', 'Wed'],
  ['thursday', 'Thursday', 'Thu'],
  ['friday', 'Friday', 'Fri'],
  ['saturday', 'Saturday', 'Sat'],
  ['sunday', 'Sunday', 'Sun'],
];

/** Every half hour of the day, as "HH:MM" → "h:MM AM". */
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  const value = `${String(h).padStart(2, '0')}:${m}`;
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ap = h < 12 ? 'AM' : 'PM';
  return { value, label: `${h12}:${m} ${ap}` };
});

type Status = 'open' | '24' | 'closed';

export interface HoursEditorProps {
  value: AvailabilityData;
  onChange: (next: AvailabilityData) => void;
}

/**
 * Compact, Calendly-style weekly-hours editor with per-day copy-to-other-days.
 *
 *   <HoursEditor value={availability} onChange={setAvailability} />
 *
 * A switch per day; an open day lists its windows as pairs of time selects,
 * with the row's actions — add a window, make it 24 hours, copy it onto other
 * days — after the first pair. The value is a plain week keyed by day name.
 */
export function HoursEditor({ value, onChange }: HoursEditorProps) {
  const setDay = (day: string, patch: Partial<DayAvailability>) =>
    onChange({ ...value, [day]: { ...value[day], ...patch } });

  const setStatus = (day: string, status: Status) => {
    const d = value[day];
    const slots = d.timeSlots.length
      ? d.timeSlots
      : [{ start: '09:00', end: '17:00' }];
    if (status === 'closed') setDay(day, { closed: true, open24Hours: false });
    else if (status === '24')
      setDay(day, { open24Hours: true, closed: false, timeSlots: slots });
    else setDay(day, { open24Hours: false, closed: false, timeSlots: slots });
  };

  const updateSlot = (
    day: string,
    index: number,
    field: 'start' | 'end',
    v: string
  ) =>
    setDay(day, {
      timeSlots: value[day].timeSlots.map((s, i) =>
        i === index ? { ...s, [field]: v } : s
      ),
    });

  const addSlot = (day: string) => {
    const slots = value[day].timeSlots;
    const last = slots[slots.length - 1];
    setDay(day, {
      timeSlots: [...slots, { start: last?.end || '09:00', end: '17:00' }],
    });
  };

  const removeSlot = (day: string, index: number) =>
    setDay(day, {
      timeSlots: value[day].timeSlots.filter((_, i) => i !== index),
    });

  /** Copy one day's full schedule onto a set of other days. */
  const copyDay = (from: string, targets: string[]) => {
    const src = value[from];
    const next = { ...value };
    for (const t of targets) {
      next[t] = {
        closed: src.closed,
        open24Hours: src.open24Hours,
        timeSlots: src.timeSlots.map((s) => ({ ...s })),
      };
    }
    onChange(next);
  };

  return (
    <Surface bordered radius="lg" tone="none" className="overflow-hidden">
      <Stack divided>
        {DAYS.map(([key, label, short]) => {
          const d = value[key];
          const status: Status = d.closed
            ? 'closed'
            : d.open24Hours
              ? '24'
              : 'open';
          const open = status !== 'closed';
          return (
            <ListRow inset="sm" key={key}>
              {/* Toggle + day label. The switch is the control and the day
                  its label, so a click on either flips it — a button around
                  a switch was a button inside a button. */}
              <Inline gap={0} className="w-[72px] shrink-0">
                <Switch
                  checked={open}
                  onCheckedChange={(next) =>
                    setStatus(key, next ? 'open' : 'closed')
                  }
                  reverse={false}
                  aria-label={`${label}: ${open ? 'open' : 'closed'}`}
                  label={
                    <Text
                      as="span"
                      variant="caption-strong"
                      tone={open ? 'inherit' : 'muted'}
                    >
                      {short}
                    </Text>
                  }
                />
              </Inline>

              {/* Schedule */}
              <Stack gap={0} grow>
                {status === 'closed' ? (
                  <Text as="span" variant="caption" tone="muted">
                    Closed
                  </Text>
                ) : status === '24' ? (
                  <Inline gap={2} align="center" justify="between">
                    <Text as="span" variant="caption-strong">
                      Open 24 hours
                    </Text>
                    <HoursRowActions
                      label={label}
                      is24
                      onToggle24={() => setStatus(key, 'open')}
                      onAddSlot={() => setStatus(key, 'open')}
                      days={DAYS.filter(([k]) => k !== key)}
                      onCopy={(targets) => copyDay(key, targets)}
                    />
                  </Inline>
                ) : (
                  <Stack gap={2}>
                    {d.timeSlots.map((slot, index) => (
                      <Inline gap={2} align="center" key={index}>
                        <TimeSelect
                          value={slot.start}
                          onChange={(v) => updateSlot(key, index, 'start', v)}
                        />
                        <Text
                          as="span"
                          variant="caption"
                          tone="muted"
                          className="shrink-0"
                        >
                          –
                        </Text>
                        <TimeSelect
                          value={slot.end}
                          onChange={(v) => updateSlot(key, index, 'end', v)}
                        />
                        {index === 0 ? (
                          <HoursRowActions
                            label={label}
                            is24={false}
                            onToggle24={() => setStatus(key, '24')}
                            onAddSlot={() => addSlot(key)}
                            days={DAYS.filter(([k]) => k !== key)}
                            onCopy={(targets) => copyDay(key, targets)}
                          />
                        ) : (
                          <Button
                            size="icon-xs"
                            variant="subtle"
                            type="button"
                            aria-label="Remove hours"
                            onClick={() => removeSlot(key, index)}
                            className="shrink-0"
                          >
                            <X className="size-icon-sm" />
                          </Button>
                        )}
                      </Inline>
                    ))}
                  </Stack>
                )}
              </Stack>
            </ListRow>
          );
        })}
      </Stack>
    </Surface>
  );
}

export interface HoursRowActionsProps {
  /** The day's full name, for the copy popover's labels. */
  label: string;
  /** The day is already open around the clock: no "add hours", and 24h lit. */
  is24: boolean;
  onToggle24: () => void;
  onAddSlot: () => void;
  /** The other days a schedule can be copied onto. */
  days: HoursDay[];
  onCopy: (targets: string[]) => void;
}

/**
 * The trailing icon cluster on a day row: add a window, toggle 24 hours, and
 * the copy-to-other-days popover.
 *
 *   <HoursRowActions label="Monday" is24={false} onAddSlot={add}
 *     onToggle24={allDay} days={otherDays} onCopy={copyTo} />
 */
export function HoursRowActions({
  label,
  is24,
  onToggle24,
  onAddSlot,
  days,
  onCopy,
}: HoursRowActionsProps) {
  return (
    <Inline gap={0} align="center" className="shrink-0">
      {!is24 && (
        <HoursIconButton label="Add hours" onClick={onAddSlot}>
          <Plus className="size-icon-sm" />
        </HoursIconButton>
      )}
      <HoursIconButton
        label={is24 ? 'Set specific hours' : 'Open 24 hours'}
        onClick={onToggle24}
        active={is24}
      >
        <Text as="span" variant="caption-strong">
          24h
        </Text>
      </HoursIconButton>
      <CopyHoursPopover label={label} days={days} onCopy={onCopy} />
    </Inline>
  );
}

export interface HoursIconButtonProps {
  /** The accessible name and the tooltip. */
  label: string;
  /** Lit as a pressed toggle — the 24h button while the day is open all day. */
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

/**
 * A row action in the hours editor: an extra-small toggle that reads as
 * pressed when `active`.
 *
 *   <HoursIconButton label="Open 24 hours" active={is24} onClick={toggle}>24h</HoursIconButton>
 */
export function HoursIconButton({
  label,
  active,
  onClick,
  children,
}: HoursIconButtonProps) {
  return (
    <Button
      variant={active ? 'primary' : 'subtle'}
      size="icon-xs"
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export interface CopyHoursPopoverProps {
  /** The day being copied from, for the trigger's name and the heading. */
  label: string;
  /** The days it can be copied onto. */
  days: HoursDay[];
  onCopy: (targets: string[]) => void;
}

/**
 * "Copy times to…" — a checklist of the other days behind a copy icon, with an
 * all-days shortcut. Applying closes the popover and clears the selection.
 *
 *   <CopyHoursPopover label="Monday" days={otherDays} onCopy={copyTo} />
 */
export function CopyHoursPopover({
  label,
  days,
  onCopy,
}: CopyHoursPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<string[]>([]);

  const toggle = (key: string) =>
    setSelected((s) =>
      s.includes(key) ? s.filter((k) => k !== key) : [...s, key]
    );

  const allSelected = selected.length === days.length;
  const apply = () => {
    if (selected.length) onCopy(selected);
    setOpen(false);
    setSelected([]);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSelected([]);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="icon-xs"
          variant="subtle"
          type="button"
          aria-label={`Copy ${label} hours to other days`}
          title="Copy to other days"
        >
          <Copy className="size-icon-sm" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" padding="sm" className="w-48">
        <Stack gap={2}>
          <Text variant="caption-strong" tone="muted">
            Copy {label} to
          </Text>
          <Checkbox
            label="All days"
            checked={allSelected}
            onCheckedChange={() =>
              setSelected(allSelected ? [] : days.map(([k]) => k))
            }
          />
          <Stack gap={1} className="max-h-52 overflow-y-auto">
            {days.map(([key, full]) => (
              <Checkbox
                key={key}
                label={full}
                checked={selected.includes(key)}
                onCheckedChange={() => toggle(key)}
              />
            ))}
          </Stack>
          <Button
            size="sm"
            fullWidth
            type="button"
            onClick={apply}
            disabled={!selected.length}
          >
            Apply{selected.length ? ` (${selected.length})` : ''}
          </Button>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}

export interface TimeSelectProps {
  /** "HH:MM", 24-hour, on the half hour. */
  value: string;
  onChange: (value: string) => void;
}

/**
 * A small select of every half hour of the day, shown as "9:00 AM".
 *
 *   <TimeSelect value="09:00" onChange={setStart} />
 */
export function TimeSelect({ value, onChange }: TimeSelectProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="min-w-0 grow">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TIME_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
